// Shared LSA / FFE PDF renderer - single source of truth for LSA.jsx.
// House style matches src/lib/raPdf.js (letterhead, navy band, tables). Do not fork.

const NAVY = '#15294a', NAVY2 = '#274a72', BLUE = '#2f6bff', INK = '#26313c'
const MUTE = '#7d8a99', LINE = '#e4e9f0', SOFT = '#f6f9fc'
const FONT = "'Trebuchet MS','Segoe UI','Helvetica Neue',Arial,sans-serif"
const OK = '#82ad8b', WARN = '#d8b871', BAD = '#cd8387'
const tOk = '#2c3b2f', tWarn = '#4a3d1c', tBad = '#452428'

const esc = v => String(v == null ? '' : v).replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]))

function fmt(d) {
  if (!d) return ''
  const dt = new Date(String(d).slice(0, 10) + 'T00:00:00')
  if (isNaN(dt)) return String(d)
  return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

function expInfo(item, today) {
  const raw = item.expiry_date || item.next_service
  if (!raw) return null
  const d = new Date(String(raw).slice(0, 10) + 'T00:00:00')
  const now = new Date(String(today).slice(0, 10) + 'T00:00:00')
  if (isNaN(d) || isNaN(now)) return null
  const days = Math.round((d - now) / 86400000)
  if (days < 0) return { key: 'exp', label: 'EXPIRED', bg: BAD, fg: tBad, date: raw }
  if (days <= 90) return { key: 'soon', label: days + 'd', bg: WARN, fg: tWarn, date: raw }
  return { key: 'ok', label: 'OK', bg: OK, fg: tOk, date: raw }
}

const STAT = {
  ok:      { label: 'OK',      bg: OK,        fg: tOk },
  caution: { label: 'CAUTION', bg: WARN,      fg: tWarn },
  defect:  { label: 'DEFECT',  bg: BAD,       fg: tBad },
  na:      { label: 'N/A',     bg: '#dfe4ea', fg: '#3a444f' },
}
const UNCHECKED = { label: 'NOT CHECKED', bg: '#eef1f5', fg: MUTE }

const wave = `<span style="display:inline-block;font-size:34px;font-weight:900;color:${BLUE};line-height:1;letter-spacing:-3px;">\u2248</span>`
const chipHtml = (t, bg, fg) => `<span style="display:inline-block;background:${bg};color:${fg};font-size:8.5px;font-weight:700;padding:3px 9px;border-radius:11px;white-space:nowrap;">${esc(t)}</span>`

function tile(n, label, tone) {
  const col = tone === 'bad' ? '#b8474f' : tone === 'warn' ? '#8a6a1e' : tone === 'good' ? '#3f6b4d' : NAVY
  return `<td style="border:1px solid ${LINE};border-radius:8px;padding:10px 12px;background:${SOFT};text-align:center;">
    <div style="font-size:20px;font-weight:700;color:${col};line-height:1.1;">${esc(n)}</div>
    <div style="font-size:8.5px;font-weight:700;letter-spacing:.07em;color:${MUTE};text-transform:uppercase;margin-top:3px;">${esc(label)}</div>
  </td>`
}

function th(t, w) {
  return `<th style="border:1px solid ${NAVY};padding:8px 7px;font-size:9px;font-weight:700;letter-spacing:.03em;color:#fff;text-transform:uppercase;text-align:left;${w ? 'width:' + w + ';' : ''}">${esc(t)}</th>`
}
function td(t, extra) {
  return `<td style="border:1px solid ${LINE};padding:8px 7px;font-size:10.5px;line-height:1.45;color:${INK};vertical-align:top;white-space:normal;word-break:break-word;${extra || ''}">${t == null || t === '' ? '\u2014' : esc(t)}</td>`
}
function tdRaw(html, extra) {
  return `<td style="border:1px solid ${LINE};padding:8px 7px;font-size:10.5px;line-height:1.45;color:${INK};vertical-align:top;text-align:center;${extra || ''}">${html}</td>`
}

/**
 * @param {object} o
 * @param {string} o.vessel        vessel name
 * @param {string} o.cat           'LSA' | 'FFE'
 * @param {string} o.mode          'check' | 'register'
 * @param {string} o.periodText    e.g. 'Jul 2026'
 * @param {string} o.groupByLabel  'Location' | 'Department' | 'Type'
 * @param {string[]} o.groupKeys
 * @param {object} o.groups        { key: item[] }
 * @param {function} o.checkOf     (item) => check row | undefined
 * @param {object} o.counts        { total, ok, caution, defect, unchecked, soon, expired }
 * @param {string} o.filterNote    human-readable active-filter line
 * @param {string} o.today         ISO ship date
 */
export function buildLsaHtml(o) {
  const isCheck = o.mode === 'check'
  const title = isCheck
    ? `${o.cat} MONTHLY CHECK RECORD`
    : `${o.cat} EQUIPMENT REGISTER`
  const c = o.counts || {}

  const summary = isCheck ? `
    <table style="width:100%;border-collapse:separate;border-spacing:6px;margin:0 -6px 20px;">
      <tr>
        ${tile(`${c.ok || 0}/${c.total || 0}`, 'Checked OK', 'good')}
        ${tile(c.caution || 0, 'Caution', (c.caution ? 'warn' : 'good'))}
        ${tile(c.defect || 0, 'Defect', (c.defect ? 'bad' : 'good'))}
        ${tile(c.unchecked || 0, 'Not checked', (c.unchecked ? 'warn' : 'good'))}
        ${tile(c.soon || 0, 'Expiring \u226490d', (c.soon ? 'warn' : 'good'))}
        ${tile(c.expired || 0, 'Expired', (c.expired ? 'bad' : 'good'))}
      </tr>
    </table>` : ''

  const head = isCheck
    ? `<tr style="background:${NAVY};">${th('No.', '4%')}${th('Item', '20%')}${th('Type', '13%')}${th('Serial', '11%')}${th('Location', '14%')}${th('Expiry', '10%')}${th('Status', '9%')}${th('Checked by', '11%')}${th('Notes', '8%')}</tr>`
    : `<tr style="background:${NAVY};">${th('No.', '4%')}${th('Item', '18%')}${th('Type', '12%')}${th('Serial', '11%')}${th('Manufacturer', '13%')}${th('Size', '7%')}${th('Location', '13%')}${th('Dept', '9%')}${th('Expiry', '7%')}${th('Next service', '6%')}</tr>`

  const cols = isCheck ? 9 : 10
  let n = 0
  const body = (o.groupKeys || []).map(gk => {
    const list = o.groups[gk] || []
    const rows = list.map(it => {
      n += 1
      const e = expInfo(it, o.today)
      const expCell = e
        ? tdRaw(`<div style="font-size:10px;color:${INK};margin-bottom:3px;">${esc(fmt(e.date))}</div>${chipHtml(e.label, e.bg, e.fg)}`)
        : td('')
      if (isCheck) {
        const chk = o.checkOf(it)
        const st = chk ? (STAT[chk.status] || UNCHECKED) : UNCHECKED
        return `<tr>
          ${td(n, `text-align:center;color:${BLUE};font-weight:700;`)}
          ${td(it.name || it.type)}${td(it.type)}${td(it.serial)}${td(it.location)}
          ${expCell}
          ${tdRaw(chipHtml(st.label, st.bg, st.fg))}
          ${td(chk ? (chk.checked_by || '') : '')}
          ${td(chk ? (chk.notes || '') : '')}
        </tr>`
      }
      return `<tr>
        ${td(n, `text-align:center;color:${BLUE};font-weight:700;`)}
        ${td(it.name || it.type)}${td(it.type)}${td(it.serial)}${td(it.manufacturer)}${td(it.size)}
        ${td(it.location)}${td(it.department)}
        ${expCell}
        ${td(fmt(it.next_service))}
      </tr>`
    }).join('')
    return `<tr><td colspan="${cols}" style="border:1px solid ${LINE};padding:8px 12px;font-size:11px;font-weight:700;letter-spacing:.03em;background:${NAVY};color:#fff;">${esc(gk)}&nbsp;&nbsp;\u00b7&nbsp;&nbsp;${list.length} item${list.length === 1 ? '' : 's'}</td></tr>${rows}`
  }).join('')

  const signoff = isCheck ? `
    <div style="margin-top:26px;page-break-inside:avoid;">
      <div style="margin:0 0 10px 0;padding-bottom:6px;border-bottom:2px solid ${NAVY};"><span style="font-size:15px;font-weight:700;color:${NAVY};">Certification</span></div>
      <div style="font-size:10.5px;line-height:1.6;color:${INK};margin-bottom:16px;">
        I certify that the ${esc(o.cat)} equipment listed above has been inspected for the period stated, that any
        defect or caution recorded has been raised as a corrective action, and that the equipment is maintained in
        accordance with the vessel's Safety Management System.
      </div>
      <table style="width:100%;border-collapse:collapse;">
        <tr>
          <td style="border:1px solid ${LINE};padding:14px 12px 26px;width:50%;vertical-align:top;">
            <div style="font-size:8.5px;font-weight:700;letter-spacing:.07em;color:${MUTE};text-transform:uppercase;margin-bottom:22px;">Checked by \u00b7 name &amp; rank</div>
            <div style="border-top:1px solid ${MUTE};padding-top:5px;font-size:9px;color:${MUTE};">Signature &nbsp;/&nbsp; Date</div>
          </td>
          <td style="border:1px solid ${LINE};padding:14px 12px 26px;width:50%;vertical-align:top;">
            <div style="font-size:8.5px;font-weight:700;letter-spacing:.07em;color:${MUTE};text-transform:uppercase;margin-bottom:22px;">Approved by \u00b7 Master</div>
            <div style="border-top:1px solid ${MUTE};padding-top:5px;font-size:9px;color:${MUTE};">Signature &nbsp;/&nbsp; Date</div>
          </td>
        </tr>
      </table>
    </div>` : ''

  return `<div style="width:960px;font-family:${FONT};color:${INK};background:#fff;padding:0;">
    <style>* { -webkit-print-color-adjust:exact; print-color-adjust:exact; box-sizing:border-box; } td,th{word-wrap:break-word;overflow-wrap:break-word;white-space:normal;} tr{page-break-inside:avoid;}</style>
    <div style="background:#fff;padding:18px 30px 14px;display:flex;justify-content:space-between;align-items:center;border-bottom:1px solid ${LINE};">
      <div style="display:flex;align-items:center;gap:13px;">${wave}<span style="font-size:22px;font-weight:800;letter-spacing:.13em;color:${NAVY};">HEYCAPTAIN</span></div>
      <div style="font-size:10px;font-weight:700;letter-spacing:.17em;color:${MUTE};text-transform:uppercase;">Safety Management System</div>
    </div>
    <div style="background:${NAVY};padding:22px 30px;display:flex;justify-content:space-between;align-items:center;">
      <div>
        <div style="font-size:24px;font-weight:700;color:#fff;letter-spacing:.02em;">${esc(title)}</div>
        <div style="font-size:12.5px;color:#aec3d6;margin-top:4px;letter-spacing:.02em;">${esc(o.vessel || '')}</div>
      </div>
      <div style="border:1.5px solid ${BLUE};border-radius:9px;padding:10px 18px;text-align:center;min-width:96px;">
        <div style="font-size:9px;color:#aec3d6;letter-spacing:.14em;margin-bottom:3px;">${isCheck ? 'PERIOD' : 'AS AT'}</div>
        <div style="font-size:15px;font-weight:700;color:#fff;">${esc(isCheck ? o.periodText : fmt(o.today))}</div>
      </div>
    </div>
    <div style="padding:24px 30px;">
      ${summary}
      <div style="display:flex;justify-content:space-between;align-items:flex-end;margin-bottom:9px;">
        <span style="font-size:15px;font-weight:700;color:${NAVY};">Grouped by ${esc(o.groupByLabel)}</span>
        <span style="font-size:9.5px;color:${MUTE};">${esc(o.filterNote || '')}</span>
      </div>
      <table style="width:100%;border-collapse:collapse;">
        <thead>${head}</thead>
        <tbody>${body || `<tr><td colspan="${cols}" style="border:1px solid ${LINE};padding:18px;text-align:center;font-size:11px;color:${MUTE};">No items match the current filter.</td></tr>`}</tbody>
      </table>
      ${signoff}
      <div style="margin-top:18px;padding-top:11px;border-top:1px solid ${LINE};display:flex;justify-content:space-between;align-items:center;font-size:8.5px;color:${MUTE};">
        <span style="font-weight:800;letter-spacing:.12em;color:${NAVY};">HEYCAPTAIN</span>
        <span style="letter-spacing:.03em;">${esc(o.vessel || '')} &middot; ${esc(o.cat)} ${isCheck ? 'check record' : 'register'} &middot; generated ${esc(fmt(o.today))}</span>
      </div>
    </div>
  </div>`
}
